import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CreditCard, Headphones, Infinity as InfinityIcon, Mail, Play, RefreshCw, ShieldCheck, ShoppingCart, Smartphone, Zap } from "lucide-react";
import { api } from "../api";
import { coverSrc } from "@/lib/cover";
import { Reveal } from "./Reveal";

const STEPS = [
  { icon: ShoppingCart, title: "Wybierz produkt", text: "Ebooki, audiobooki, aplikacje i gry — pojedynczo albo w całym pakiecie w lepszej cenie." },
  { icon: CreditCard, title: "Zapłać bezpiecznie", text: "Kartą, BLIKIEM lub przelewem. Konto klienta założymy za Ciebie przy pierwszym zamówieniu." },
  { icon: Mail, title: "Otrzymaj od razu", text: "Po potwierdzeniu płatności produkt trafia na Twój e-mail i do biblioteki w koncie klienta." },
];

const TRUST = [
  { icon: Zap, text: "Dostawa natychmiast po płatności" },
  { icon: InfinityIcon, text: "Kupujesz raz — masz na zawsze" },
  { icon: RefreshCw, text: "Aktualizacje w cenie" },
  { icon: ShieldCheck, text: "Bezpieczne płatności" },
];

const FORMAT_ICONS = { ebook: BookOpen, audiobook: Headphones, aplikacja: Smartphone };

function HowItWorks() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6" data-testid="how-it-works">
      <Reveal>
        <div className="text-center">
          <h2 className="font-display text-xl font-bold sm:text-2xl">Jak to <span className="gradient-text">działa?</span></h2>
          <p className="mt-2 text-sm text-slate-400">Trzy proste kroki od wyboru do pierwszej lekcji lub pierwszej strony.</p>
        </div>
      </Reveal>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {STEPS.map((s, i) => (
          <Reveal key={s.title} delay={i * 120}>
            <div className="relative h-full rounded-2xl glass-panel p-5">
              <span className="absolute -top-3 left-5 rounded-full bg-gradient-to-r from-cyan-500 to-teal-400 px-2.5 py-0.5 text-[11px] font-extrabold text-slate-950">Krok {i + 1}</span>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-400/15 text-cyan-300"><s.icon className="h-5 w-5" /></span>
              <div className="mt-3 text-sm font-bold text-slate-100">{s.title}</div>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={200}>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5">
          {TRUST.map((t) => (
            <span key={t.text} className="flex items-center gap-2 text-xs font-semibold text-slate-300"><t.icon className="h-4 w-4 text-teal-300" /> {t.text}</span>
          ))}
        </div>
      </Reveal>
    </section>
  );
}

function Library({ items }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6" data-testid="home-library">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold sm:text-2xl">Twoja <span className="gradient-text">biblioteka</span></h2>
            <p className="mt-1 text-sm text-slate-400">Produkty, do których masz dostęp — otwórz i korzystaj od razu.</p>
          </div>
          <Link to="/konto" className="flex items-center gap-1.5 text-sm font-semibold text-cyan-300 hover:underline" data-testid="home-library-all">
            Cała biblioteka <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.slice(0, 6).map((i, idx) => {
          const Icon = FORMAT_ICONS[i.format] || BookOpen;
          return (
            <Reveal key={i.id} delay={idx * 80}>
              <div className="flex gap-4 rounded-2xl glass-panel p-4" data-testid={`home-library-item-${i.id}`}>
                {i.cover_image ? (
                  <img src={coverSrc(i)} alt="" loading="lazy" className="h-20 shrink-0 rounded-xl object-cover shadow-lg" style={{ width: 60 }} />
                ) : (
                  <div className="flex h-20 shrink-0 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-400" style={{ width: 60 }}><Icon className="h-6 w-6" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-slate-100">{i.title}</div>
                  <div className="text-xs text-slate-500">{i.format}{i.via ? " · z pakietu" : ""}</div>
                  {i.files && i.files.length > 0 ? (
                    <Link to="/konto" data-testid={`home-library-files-${i.id}`} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 px-3.5 py-1.5 text-xs font-bold text-slate-950 transition-transform hover:scale-105">
                      <Play className="h-3.5 w-3.5" /> Pobierz i słuchaj
                    </Link>
                  ) : i.coming_soon ? (
                    <span className="mt-2 inline-block rounded-lg border border-violet-400/40 bg-violet-400/10 px-3 py-1 text-[11px] font-bold text-violet-300">Wkrótce dostępne</span>
                  ) : (
                    <a href={i.link} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 px-3.5 py-1.5 text-xs font-bold text-slate-950 transition-transform hover:scale-105">
                      <Play className="h-3.5 w-3.5" /> {i.format === "aplikacja" ? "Otwórz aplikację" : "Otwórz"}
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

// Pod głównym banerem: goście widzą "Jak to działa", zalogowani klienci z zakupami — swoją prawdziwą bibliotekę
export function HomeLibraryStrip() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let token = "";
    try { token = localStorage.getItem("dl_customer_token") || ""; } catch { /* brak localStorage */ }
    if (!token) return;
    api.get("/customer/library", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  if (items && items.length > 0) return <Library items={items} />;
  return <HowItWorks />;
}
